/**
 * Pages to FTUX: a page reports what the owner just did in the product, one line at the real success handler
 * (ftuxSignal with the action id), and the piece in play follows. While no piece listens a signal does nothing, so
 * outside the flow every page behaves exactly as before.
 */
import React from 'react';

import type { FtuxAction } from './catalog';
import { createHeldFlag } from './shell/heldFlag';

export type FtuxSignalListener = (action: FtuxAction) => void;

const listeners = new Set<FtuxSignalListener>();

/**
 * A listener's error goes to the console and the error reporting on a task of its own, never into the page handler
 * that signalled: FTUX must never break the page.
 */
const rethrowLater = (error: unknown): void => {
  setTimeout(() => {
    throw error;
  }, 0);
};

/** Reports what the owner did to the FTUX (a no-op while nothing listens). */
export const ftuxSignal = (action: FtuxAction): void => {
  // A snapshot: a listener that subscribes or leaves meanwhile changes the next signal, not this one.
  Array.from(listeners).forEach((listener) => {
    try {
      listener(action);
    } catch (error) {
      rethrowLater(error);
    }
  });
};

/**
 * Listens to the pages' signals while `enabled`. The latest `listener` is called without subscribing again, and
 * listeners hear a signal in the order they subscribed.
 */
export const useFtuxSignal = (listener: FtuxSignalListener, enabled = true): void => {
  const latestRef = React.useRef(listener);
  latestRef.current = listener;

  React.useEffect(() => {
    if (!enabled) return undefined;
    const relay: FtuxSignalListener = (action) => latestRef.current(action);
    listeners.add(relay);
    return () => {
      listeners.delete(relay);
    };
  }, [enabled]);
};

/** FTUX to Game Lists: the games piece holds the micro drag on (a game carried to the front and back, nothing saved). */
const dragDemo = createHeldFlag();

/** Plays Game Lists' micro drag again and again (true), or stops it (false). */
export const setDragDemo = dragDemo.set;

/** Whether the FTUX holds Game Lists' micro drag on. */
export const useDragDemoOn = dragDemo.useHeld;

/** FTUX to Game Lists: a piece lights the open list's games (the games piece), or the page's column of lists (the
 *  list piece's Create a new list). Drilled down (a phone, a narrow window), the tab opens on that level, not on the
 *  site's pages, so the piece finds its zone. */
const gamesShown = createHeldFlag();
const listsShown = createHeldFlag();

/** Holds Game Lists on the open list's games (true), or lets it go (false). */
export const setGamesShown = gamesShown.set;
export const useGamesShown = gamesShown.useHeld;
/** Holds Game Lists on the page's column of lists (true), or lets it go (false). */
export const setListsShown = listsShown.set;
export const useListsShown = listsShown.useHeld;
